/* eslint-disable max-lines */
import React, { useState, useEffect, useRef, useCallback, useImperativeHandle } from 'react';
import type { RouteComponentProps } from 'react-router-dom';
import { styled } from '@this/constants/themes';
import _ from 'lodash';

import { Fetcher, HTTPError, trackClick, trackEvent, trackView, appendScript } from '@this/src/util';
import { reportError } from '@this/lib/bugsnag';
import { observer } from 'mobx-react';
import type { ReserveConfirmStoreResponseArgs } from '@this/src/domain/reserve_confirm_store';
import ReserveConfirmStore, {
  convertReserveConfirmStoreResponseToArgs
} from '@this/src/domain/reserve_confirm_store';
import type Service from '@this/domain/service';
import ReserveConfirmPcTemplate from './reserve_confirm_pc.template';
import ReserveConfirmSpTemplate from './reserve_confirm_sp.template';

export const steps = ['Step 1. 入力', 'Step 2. 確認', 'Step 3. 完了'];

interface PaymentResponse {
  firstTrip: boolean;
  trip_id: never;
}

interface GmoTokenResult {
  tokenObject: {
    token: string;
    toBeExpiredAt: string;
    maskedCardNo: string;
    isSecurityCodeSet: boolean;
  };
  resultCode: string;
}

interface Props extends RouteComponentProps {
  service: Service;
}

export interface ReserveConfirmHandle {
  handlePaymentSubmit: () => void;
}

const ReserveConfirm = React.forwardRef<ReserveConfirmHandle, Props>(({ service, history }, ref) => {
  const tokenRef = useRef<HTMLInputElement>(null);

  const [store, setStore] = useState<ReserveConfirmStore | null>(null);
  const [, setLoading] = useState<boolean>(true);
  const [, setReturning] = useState<boolean>(false);
  const [, setShowError] = useState<boolean>(false);
  const [, setItemsBlankError] = useState<boolean>(false);
  const [, setResponseError] = useState<string | undefined>(undefined);

  const handleToken = useCallback((result: GmoTokenResult) => {
    if (tokenRef.current) {
      $(tokenRef.current).trigger('onChange', result);
    }
  }, []);

  const handlePaymentSucceed = useCallback((result: PaymentResponse) => {
    const queryFirst = result.firstTrip ? '?first=true' : '';
    const url = `/trips/${result.trip_id}${queryFirst}`;
    location.href = url;
  }, []);

  const handleTokenChange = useCallback(
    (e: unknown, result: GmoTokenResult) => {
      if (result.resultCode === '000') {
        if (!store) return;
        store.paymentFailed = false;
        const params = _.merge(store?.reserveInfo.submitParams, {
          token: result.tokenObject.token
        });
        Fetcher.post<PaymentResponse>('/payments/token.json', params).then(
          paymentResult => {
            trackEvent('Payment succeed');
            handlePaymentSucceed(paymentResult);
          },
          error => {
            let errorMessages;
            trackEvent('Payment failed');
            if (error instanceof HTTPError && error.response?.data?.errors) {
              errorMessages = error.response?.data?.errors;
            } else {
              errorMessages = '通信環境が不安定です。\n時間をおいてもう一度お試しください。';
            }
            if (!store) {
              return;
            }
            store.paymentLoading = false;
            store.paymentFailed = true;
            store.paymentErrors = errorMessages;
          }
        );
      } else if (result.resultCode === '901') {
        if (!store) {
          return;
        }
        store.paymentLoading = false;
        store.paymentFailed = true;
        store.paymentErrors = '通信環境が不安定です。\n時間をおいてもう一度お試しください。';
      } else {
        if (!store) {
          return;
        }
        store.paymentLoading = false;
        store.paymentFailed = true;
        store.paymentErrors = '決済に失敗しました。\n入力内容に誤りがないかご確認ください。';
        store.paymentFailed = false;
      }
    },
    [store?.reserveInfo, handlePaymentSucceed]
  );

  const facebookAdConversion = useCallback(() => {
    const totalPrice = store?.reserveInfo?.reservingTrip.totalPrice;
    if (totalPrice != null) {
      fbq('track', 'Purchase', {
        value: totalPrice,
        currency: 'JPY'
      });
    }
  }, [store?.reserveInfo]);

  const requestCardSeq = useCallback(() => {
    if (!store?.reserveInfo) return;

    const params = _.merge(store.reserveInfo.submitParams, {
      payment_transaction: store.reserveInfo.paymentTransaction
    });

    Fetcher.post<PaymentResponse>('/payments/card_seq.json', params).then(
      result => {
        trackEvent('Payment succeed');
        facebookAdConversion();
        handlePaymentSucceed(result);
      },
      error => {
        let errorMessages;
        trackEvent('Payment failed');
        if (error instanceof HTTPError && error.response?.data?.errors) {
          errorMessages = error.response?.data?.errors;
        } else {
          errorMessages = approvalErrorMessage();
        }
        store.paymentLoading = false;
        store.paymentFailed = true;
        store.paymentErrors = errorMessages;
      }
    );
  }, [store, facebookAdConversion, handlePaymentSucceed]);

  const setSonyPaymentTokenCallback = useCallback(
    (token: string, _maskedCard: string) => {
      if (store?.reserveInfo) {
        store.reserveInfo.paymentTransaction = { token, uid: 'new' };
        requestCardSeq();
      }
    },
    [store?.reserveInfo, requestCardSeq]
  );

  useEffect(() => {
    if (store) {
      return;
    }

    const url = new URL(location.href);
    const searchQueryId = url.searchParams.get('search_query_id') ?? '';

    if (location.hash && location.hash.length > 0) {
      location.href = `/reserve_confirm?search_query_id=${searchQueryId}`;
      return;
    }
    Fetcher.get<ReserveConfirmStoreResponseArgs>(
      `/reserve_confirm/show.json?search_query_id=${searchQueryId}&${Date.now()}`
    )
      .then(async result => {
        const s = new ReserveConfirmStore(
          convertReserveConfirmStoreResponseToArgs(result, { isSimpleRequest: false, fromTripForm: false })
        );
        await s.reserveInfo.fetchApproveItems('before_business_trip');
        setStore(s);
        if (s.reserveInfo.paymentGatewayType === 'gmo') {
          appendScript(`https://${s.tokenHost}/ext/js/token.js`);
          if (tokenRef.current) {
            $(tokenRef.current).bind('onChange', handleTokenChange);
          }
        }

        if (s.reserveInfo.paymentGatewayType === 'sony') {
          const scriptUrl = s.sonyPaymentGatewayTokenGenerateScriptUrl;
          if (scriptUrl) {
            const script = document.createElement('script');
            script.type = 'text/javascript';
            script.src = scriptUrl;
            script.setAttribute('callBackFunc', 'setSonyPaymentToken');
            script.className = 'spsvToken';
            document.head.appendChild(script);

            window.setSonyPaymentToken = setSonyPaymentTokenCallback as (
              token: string,
              maskedCard: string
            ) => void;
          }
        }
        trackView('/reserve_confirm#1');

        setLoading(false);
      })
      .then(
        () => {},
        error => {
          if (error instanceof HTTPError && error.response?.status === 400) {
            const message = error.response.data.error;
            if (message) {
              if (message === 'items_blank') {
                setItemsBlankError(true);
                setLoading(false);
              } else {
                setShowError(true);
                setLoading(false);
                setResponseError(message);
              }
            } else {
              setReturning(true);
              setLoading(false);
            }
          } else {
            setShowError(true);
            setLoading(false);
            setResponseError(error.response.data.error);
          }
        }
      );
  }, [store, handleTokenChange, setSonyPaymentTokenCallback]);

  useEffect(() => {
    return () => {
      if (store?.reserveInfo?.paymentGatewayType === 'sony') {
        window.setSonyPaymentToken = null;
      }
    };
  }, [store?.reserveInfo?.paymentGatewayType]);

  // openSelectTravelers(index: never, e: Event) {
  //   e.preventDefault();
  //   this.setState({
  //     selectedIndex: index,
  //     showSelectTravelers: true
  //   });
  // }

  // closeSelectTravelers(e: Event) {
  //   e.preventDefault();
  //   this.setState({ showSelectTravelers: false });
  // }

  const handleBackToSelectClick = useCallback(() => {
    window.scrollTo(0, 0);
    setTimeout(() => {
      history.push(store?.selectPageUrl || '/dashboard');
    }, 100);
  }, [history, store?.selectPageUrl]);

  const handlePaymentSubmit = useCallback(() => {
    trackClick('Payment submit');
    if (!store) return;
    store.paymentLoading = true;

    if (!store?.reserveInfo) return;
    if (!store.reserveInfo.canPayment) {
      store.paymentLoading = false;
      store.paymentFailed = true;
      store.paymentErrors = 'こちらの旅程は手配依頼できません。';
      reportError(new Error('こちらの旅程は手配依頼できません。'));
    }
    if (store.reserveInfo.paymentMethodType === 'card' && store.reserveInfo.paymentGatewayType === 'gmo') {
      if (tokenRef.current) {
        $(tokenRef.current).unbind('onChange');
        $(tokenRef.current).bind('onChange', handleTokenChange);
      }

      if (store.reserveInfo.paymentTransaction?.uid === 'new') {
        if (typeof Multipayment !== 'undefined') {
          Multipayment.init(store.shopId);
          Multipayment.getToken(store.reserveInfo.newCardParams, handleToken);
        } else {
          store.paymentLoading = false;
          store.paymentFailed = true;
          store.paymentErrors = approvalErrorMessage();
          reportError(new Error('Multipayment does not exist'));
        }

        return;
      }
    }

    if (store.reserveInfo.paymentMethodType === 'card' && store.reserveInfo.paymentGatewayType === 'sony') {
      if (store.reserveInfo.paymentTransaction?.uid === 'new') {
        const cardParams = store.reserveInfo.newCardParams;
        SpsvApi.spsvCreateToken(
          cardParams.cardno,
          cardParams.expireYear,
          cardParams.expireMonth,
          cardParams.securitycode,
          '',
          '',
          '',
          '',
          ''
        );
      } else {
        requestCardSeq();
        return;
      }
    }

    if (store.reserveInfo.paymentMethod === 'billing') {
      Fetcher.post<PaymentResponse>('/payments/billing.json', store.reserveInfo.submitParams).then(
        result => {
          trackEvent('Billing succeed');
          facebookAdConversion();
          handlePaymentSucceed(result);
        },
        error => {
          let errorMessages;
          trackEvent('Billing failed');
          if (error instanceof HTTPError && error.response?.data?.errors) {
            errorMessages = error.response?.data?.errors;
          } else {
            errorMessages = approvalErrorMessage();
          }
          store.paymentLoading = false;
          store.paymentFailed = true;
          store.paymentErrors = errorMessages;
        }
      );
      return;
    }
    if (store.reserveInfo.paymentMethodType === 'card' && store.reserveInfo.paymentGatewayType === 'gmo') {
      requestCardSeq();
    }
  }, [store, handleTokenChange, handleToken, requestCardSeq, facebookAdConversion, handlePaymentSucceed]);

  useImperativeHandle(
    ref,
    () => ({
      handlePaymentSubmit
    }),
    [handlePaymentSubmit]
  );

  const approvalErrorMessage = () => {
    return `申し訳ございません。ご予約を受け付けることができませんでした。

時間を空けてもう一度お試しいただき、改善されない場合は
[お問い合わせフォーム](/inquiries/new)よりご連絡ください。`;
  };

  try {
    return (
      <Confirm>
        {/* onChangeは第2引数とらないのでhandleTokenChangeの型が使えない */}
        <input id="token" type="hidden" onChange={handleTokenChange as any} ref={tokenRef} />
        <ConfirmPc>
          <ReserveConfirmPcTemplate
            service={service}
            store={store}
            handleBackToSelectClick={handleBackToSelectClick}
            handlePaymentSubmit={handlePaymentSubmit}
          />
        </ConfirmPc>
        <ConfirmSp>
          <ReserveConfirmSpTemplate
            service={service}
            store={store}
            handleBackToSelectClick={handleBackToSelectClick}
            handlePaymentSubmit={handlePaymentSubmit}
          />
        </ConfirmSp>
      </Confirm>
    );
  } catch (e) {
    reportError(e as Error);
    return null;
  }
});

const baseColor = '#af985e';
const blackColor = '#292929';
const grayColor = '#eee';

const Confirm = styled.div`
  min-height: 100vh;
`;

const ConfirmPc = styled.div`
  display: block;

  min-height: 100vh;

  @media (max-width: 1000px) {
    display: none;
  }

  > :first-child {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    margin: 0 auto;
  }

  header {
    background: ${blackColor};
    border-bottom: 1px solid ${baseColor};
  }

  .header-in {
    max-width: 100%;
    background: none;
    border-bottom: none;
    padding: 0 20px;
  }

  .logo {
    padding-left: 0;
  }

  .trips-button {
    margin-right: 0;
  }

  .in-advance-button {
    margin-right: 10px;
  }

  .info-content {
    position: relative;
    background: white;

    &__title {
      &__link {
        position: absolute;
        top: 10px;
        right: 10px;
        margin: 0;
      }
    }
  }
`;

const ConfirmSp = styled.div`
  display: none;

  min-height: 100vh;

  @media (max-width: 1000px) {
    display: block;
  }

  > :first-child {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    margin: 0 auto;
  }

  .header-in {
    max-width: 100%;
  }

  .content-wrapper {
    flex-grow: 9999;
    display: flex;
    flex-direction: column;
    width: 100%;
    background: ${grayColor};
  }

  .content-body {
    max-width: 100%;
    padding: 10px;
  }
`;

export default observer(ReserveConfirm);
